import _ from 'underscore';

import {assertAndContinue} from 'Asserts';
import excess from 'notebook/tables/excess';
import SpreadsheetEvalMixin from 'notebook/tables/spreadsheets/SpreadsheetEvalMixin';
import TableUtil from 'notebook/tables/TableUtil';

export function UNSUPPORTED_OPERATION() {
  assertAndContinue(false, 'Unsupported operation.');
}

export const SELECTORS_LINK = {
  // Selectors are not enabled, but TableEditable still requires this
  value: [],
  selectorUsageCounts: {},
};

/**
 * Optimize table for spreadsheet evaluation.
 *
 * SpreadsheetEvalMixin has performance optimization where it checks for deep equality of the old table and
 * new table. However, in many cases the table also includes rendering information (JSX), which
 * is not guaranteed to be the same. Since this information does not have an impact on computation,
 * we simply strip it out.
 *
 * TODO(BNCH-4552): The evaluator should not take in a full table object, but only the essentials for
 *   performing evaluation (rowData, table, columnConstraints).
 */
function prepareForComputation(table) {
  if (table._rowHeader) {
    // eslint-disable-next-line no-restricted-syntax
    table = _.omit(table, '_rowHeader');
  }
  if (table.rowLabels) {
    // eslint-disable-next-line no-restricted-syntax
    table = _.omit(table, 'rowLabels');
  }
  return table;
}

/**
 * Classes should implement getTable, getTableCid, handleTableUpdate, shouldDisableFormulas, spreadsheetRoot
 */
export const SimpleSpreadsheetEvalMixin = {
  mixins: [SpreadsheetEvalMixin],

  /**
   * Accessed externally via a ref.
   */
  getComputedTable() {
    return this.state.computedTables[this.getTable(this.props).name];
  },

  /* Required by SpreadsheetEvalMixin */
  makeFullTableMap(props) {
    return {
      [this.getTable(props).name]: prepareForComputation(this.getTable(props)),
    };
  },

  /* Required by SpreadsheetEvalMixin */
  selectorsFromProps(props) {
    return SELECTORS_LINK;
  },

  _isMultiEditing() {
    return this.state.editingTable === this.getTable(this.props).name;
  },

  insertSimpleTableColumnsLeft(index, start, end, columnInfo = {}) {
    const {selection, table} = this._performSimpleTableOp(
      (tableMap, computedTables, targetTableName) =>
        excess.ops.insertColumnsLeft(tableMap, targetTableName, start, end, columnInfo),
      {forColumnEdit: true}
    );
    this.handleTableUpdate(index, {table, selection});
  },

  insertSimpleTableColumnsRight(index, start, end, columnInfo = {}) {
    const {selection, table} = this._performSimpleTableOp(
      (tableMap, computedTables, targetTableName) =>
        excess.ops.insertColumnsRight(tableMap, targetTableName, start, end, columnInfo),
      {forColumnEdit: true}
    );
    this.handleTableUpdate(index, {table, selection});
  },

  reorderSimpleTableRowsOrColumns(index, options) {
    const {selection, table} = this._performSimpleTableOp(
      (tableMap, computedTables, targetTableName) =>
        excess.ops.reorderRowsOrColumns(tableMap, targetTableName, options),
      {forColumnEdit: true}
    );
    this.handleTableUpdate(index, {table, selection});
  },

  deleteSimpleTableColumns(index, start, end) {
    const {selection, table} = this._performSimpleTableOp(
      (tableMap, computedTables, targetTableName) =>
        excess.ops.deleteColumns(tableMap, targetTableName, start, end),
      {forColumnEdit: true}
    );
    this.handleTableUpdate(index, {table, selection});
  },

  deleteSimpleTableRows(index, start, end) {
    const {selection, table} = this._performSimpleTableOp(
      (tableMap, computedTables, targetTableName) =>
        excess.ops.deleteRows(tableMap, targetTableName, start, end),
      {forRowRemoval: true}
    );
    this.handleTableUpdate(index, {table, selection});
  },

  renameSimpleTable(index, newTableName) {
    const {selection, table} = this._performSimpleTableOp((tableMap, computedTable, targetTableName) =>
      excess.ops.renameTable(tableMap, targetTableName, newTableName)
    );
    this.handleTableUpdate(index, {table, selection});
  },

  _performSimpleTableOp(op, options) {
    // Apply the operation to retrieve the new table map, along with the desired selection.
    const oldTable = this.getTable(this.props);
    const oldTables = {[oldTable.name]: oldTable};
    const {tables: newTables, box} = op(oldTables, this.state.computedTables, oldTable.name);
    const newTable = newTables[oldTable.name];
    if (TableUtil.isInvalidOpWithWarn(oldTable, newTable, options)) {
      return null;
    }
    const selection = !box
      ? null
      : {
          multiSelectKey: TableUtil.getTableMultiSelectKey(this.getTableCid()),
          data: {
            multiActive: false,
            multiSelIndex: [box.minI, box.minJ],
            multiStartIndex: [box.minI, box.minJ],
            multiEndIndex: [box.maxI, box.maxJ],
            multiActualIndex: [box.minI, box.minJ],
          },
        };
    return {selection, table: newTable};
  },

  getTableEditableProps() {
    return {
      insertColumnsLeft: this.insertSimpleTableColumnsLeft,
      insertColumnsRight: this.insertSimpleTableColumnsRight,
      deleteColumns: this.deleteSimpleTableColumns,
      deleteRows: this.deleteSimpleTableRows,
      insertRowsAbove: UNSUPPORTED_OPERATION,
      insertRowsBelow: UNSUPPORTED_OPERATION,
      reorderRowsOrColumns: this.reorderSimpleTableRowsOrColumns,
      renameTable: this.renameSimpleTable,
      selectorsLink: SELECTORS_LINK,
      selectorsEnabled: false,
      multiEditing: this._isMultiEditing(),
    };
  },
};
